import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUndoStore } from '../lib/undoStore';

/**
 * Snackbar am unteren Bildschirmrand mit "Rückgängig"-Knopf.
 * Wird via useUndoStore.show() angetriggert. Schliesst sich nach
 * `ttlMs` automatisch (Server-Soft-Delete bleibt dann bestehen).
 */
export function UndoToast() {
  const current = useUndoStore((s) => s.current);
  const trigger = useUndoStore((s) => s.trigger);
  const dismiss = useUndoStore((s) => s.dismiss);

  const [progress, setProgress] = useState(1);

  useEffect(() => {
    if (!current) return;
    const start = Date.now();
    const total = current.ttlMs;
    const id = current.id;

    // Auto-dismiss nach ttlMs
    const dismissTimer = window.setTimeout(() => dismiss(id), total);

    // Progress-Bar-Animation per requestAnimationFrame (sauberer als setInterval)
    let raf = 0;
    const tick = () => {
      const elapsed = Date.now() - start;
      const ratio = Math.max(0, 1 - elapsed / total);
      setProgress(ratio);
      if (ratio > 0) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.clearTimeout(dismissTimer);
      cancelAnimationFrame(raf);
    };
  }, [current, dismiss]);

  if (!current) return null;

  return createPortal(
    <div
      className="undo-toast"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 'calc(env(safe-area-inset-left) + 16px)',
        right: 'calc(env(safe-area-inset-right) + 16px)',
        bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        zIndex: 600,
        margin: '0 auto',
        maxWidth: 460,
        background: 'var(--color-navy)',
        color: '#fff',
        borderRadius: 10,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, fontSize: 14 }}>{current.label}</div>
      <button
        type="button"
        onClick={() => trigger(current.id)}
        style={{
          background: 'transparent',
          color: 'var(--color-primary-light)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          borderRadius: 6,
          padding: '6px 14px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        Rückgängig
      </button>
      <button
        type="button"
        onClick={() => dismiss(current.id)}
        aria-label="Schliessen"
        style={{
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.7)',
          border: 'none',
          fontSize: 18,
          cursor: 'pointer',
          padding: 0,
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
      {/* Progress-Bar zeigt verbleibende Zeit */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 2,
          width: `${progress * 100}%`,
          background: 'var(--color-primary-light)',
          transition: 'width 0.1s linear',
        }}
      />
    </div>,
    document.body,
  );
}
